import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../store';
import { selectAuthUser } from '../store/slices/auth/auth.selector';

/**
 * Central hook for all PMO route navigation.
 * Reads orgSlug from Redux so callers never need to manually concatenate it.
 *
 * Usage:
 *   const { toProjects, toBacklog, toBoard, toTask } = usePmoNavigation();
 *   toBacklog(projectId);  // navigates to /{orgSlug}/pmo/{projectId}/backlog
 */
export const usePmoNavigation = () => {
  const navigate = useNavigate();
  const user = useAppSelector(selectAuthUser);
  const orgSlug = user?.orgSlug ?? 'org';

  const base = (projectKey: string) => `/${orgSlug}/pmo/${projectKey}`;

  return {
    orgSlug,

    /** /{orgSlug}/pmo/projects */
    toProjects: (replace = false) =>
      navigate(`/${orgSlug}/pmo/projects`, { replace }),

    /** /{orgSlug}/pmo/create */
    toCreateProject: (replace = false) =>
      navigate(`/${orgSlug}/pmo/create`, { replace }),

    /** /{orgSlug}/pmo/edit (with project in location.state) */
    toEditProject: (project?: any, replace = false) =>
      navigate(`/${orgSlug}/pmo/edit`, { replace, state: { project } }),

    /** /{orgSlug}/pmo/settings/convert-to-scrum (with project in location.state) */
    toConvertToScrum: (project?: any, replace = false) =>
      navigate(`/${orgSlug}/pmo/settings/convert-to-scrum`, { replace, state: { project } }),

    /** /{orgSlug}/pmo/{projectKey}/backlog, with an optional search such as "?epic=..." */
    toBacklog: (projectKey: string, replace = false, search = '') =>
      navigate({ pathname: `${base(projectKey)}/backlog`, search }, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/board */
    toBoard: (projectKey: string, replace = false) =>
      navigate(`${base(projectKey)}/board`, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/active-sprint (Sprint projects only), with an optional search */
    toActiveSprint: (projectKey: string, replace = false, search = '') =>
      navigate({ pathname: `${base(projectKey)}/active-sprint`, search }, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/sprint-history (Sprint projects only) */
    toSprintHistory: (projectKey: string, replace = false) =>
      navigate(`${base(projectKey)}/sprint-history`, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/epics (Sprint projects only) */
    toEpics: (projectKey: string, replace = false) =>
      navigate(`${base(projectKey)}/epics`, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/epics/{epicKey} (Sprint projects only) */
    toEpic: (projectKey: string, epicKey: string, replace = false) =>
      navigate(`${base(projectKey)}/epics/${epicKey}`, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/{workItemKey} */
    toTask: (projectKey: string, workItemKey: string, replace = false) =>
      navigate(`${base(projectKey)}/${workItemKey}`, { replace }),

    /** /{orgSlug}/pmo/{projectKey}/create-task */
    toCreateTask: (projectKey: string, query?: string, replace = false) =>
      navigate(`${base(projectKey)}/create-task${query ? `?${query}` : ''}`, { replace }),

    /** Build a raw URL string (for breadcrumbs / Link to= props) */
    buildProjectsUrl: () => `/${orgSlug}/pmo/projects`,
    buildBacklogUrl: (projectKey: string) => `${base(projectKey)}/backlog`,
    buildBoardUrl: (projectKey: string) => `${base(projectKey)}/board`,
    buildActiveSprintUrl: (projectKey: string) => `${base(projectKey)}/active-sprint`,
    buildSprintHistoryUrl: (projectKey: string) => `${base(projectKey)}/sprint-history`,
    buildEpicsUrl: (projectKey: string) => `${base(projectKey)}/epics`,
    buildEpicUrl: (projectKey: string, epicKey: string) => `${base(projectKey)}/epics/${epicKey}`,
    buildTaskUrl: (projectKey: string, workItemKey: string) => `${base(projectKey)}/${workItemKey}`,
    buildCreateProjectUrl: () => `/${orgSlug}/pmo/create`,
    buildEditProjectUrl: () => `/${orgSlug}/pmo/edit`,
    buildCreateTaskUrl: (projectKey: string) => `${base(projectKey)}/create-task`,
    buildProjectUrl: (projectKey: string) => base(projectKey),
  };
};
